import React from "react";
import ReactDOM from "react-dom/client";
import { formatDate } from "../../../../app/formatters/DateFormatter";
import { SPORT_TYPE } from "../../enums";

const STATUS_BADGE_CLASSES = {
  COMPLETED: "badge badge--success",
  NONE_COMPLETED: "badge badge--danger",
  WAITING_LIST: "badge badge--warning",
  DELETED: "badge badge--danger"
};

/** @typedef {import('../../transformers/CfcRegistrationTransformer.js').CfcRegistrationObj } CfcRegistrationObj */
/** @typedef {import('../../models/CfcCategoryModel.js').CfcCategoryItem } CfcCategoryItem */
/** @typedef {import('../../models/CfcRaceModel.js').CfcRaceItem } CfcRaceItem */
/** @typedef {import('../../models/CfcTournamentModel.js').CfcTournamentItem } CfcTournamentItem */

/**
 * @typedef {object} CfcRegistrationsBackProps
 * @property {?string} id - ID du tournoi pour lequel on veut récupérer les inscriptions
 * @property {string} noCoordinateLabel - Libellé affiché quand l'athlète n'a pas de box / club
 * @property {Array<CfcRegistrationObj>} cfcRegistrations - Liste des inscriptions
 * @property {Array<CfcCategoryItem>} cfcCategories - Liste des catégories
 * @property {Array<CfcRegistrationObj>} filteredCfcRegistrations - Liste des inscriptions filtrées
 * @property {{ cfcCategoryId: ?number, searchValue: ?string }} search - Objet contenant les valeurs de recherche
 */

class CfcRegistrationsBack extends React.Component {
  /**
   * @param {CfcRegistrationsBackProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      id: props.id || null,
      cfcTournament: /** @type {CfcTournamentItem} */ ({}),
      cfcRegistrations: /** @type {Array<CfcRegistrationObj>} */ ([]),
      filteredCfcRegistrations: /** @type {Array<CfcRegistrationObj>} */ ([]),
      cfcCategories: /** @type {Array<CfcCategoryItem>} */ ([]),
      cfcRaces: /** @type {Array<CfcRaceItem>} */ ([]),
      search: {
        cfcCategoryId: null,
        cfcRaceId: null,
        searchValue: null
      }
    };
  }

  async componentDidMount() {
    const cfcRegistrations = await this.fetchCfcRegistrations();
    const cfcCategories = await this.fetchCfcCategories();
    const cfcRaces = await this.fetchCfcRaces();
    const cfcTournament = cfcRegistrations[0]?.cfc_tournament;

    this.setState({ cfcRegistrations, cfcCategories, cfcRaces, cfcTournament, filteredCfcRegistrations: cfcRegistrations });
  }

  /**
   * Permet de récupérer la liste des inscriptions d'une compétition
   * @returns {Promise<Array<CfcRegistrationObj>>} - Liste des inscriptions
   */
  async fetchCfcRegistrations() {
    var response = await fetch(`/api/cfc-registration/search?cfcTournamentId=${this.state.id}&allRegistrations=true`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des catégories d'une compétition
   * @returns {Promise<Array<CfcCategoryItem>>} - Liste des catégories
   */
  async fetchCfcCategories() {
    var response = await fetch(`/api/cfc-category/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des distances d'une compétition
   * @returns {Promise<Array<CfcRaceItem>>} - Liste des distances
   */
  async fetchCfcRaces() {
    var response = await fetch(`/api/cfc-race/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Lorsqu'on change la valeur du select, on filtre les inscriptions en fonction de la catégorie sélectionnée
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le select
   */
  onChangeOnSelectCategory = event => {
    const cfcCategoryId = event.currentTarget.value ? parseInt(event.currentTarget.value, 10) : null;

    this.setState(
      {
        search: {
          ...this.state.search,
          cfcCategoryId
        }
      },
      () => this.refreshList()
    );
  };

  /**
   * Lorsqu'on change la valeur du select, on filtre les inscriptions en fonction de la distance sélectionnée
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le select
   */
  onChangeOnSelectRace = event => {
    const cfcRaceId = event.currentTarget.value ? parseInt(event.currentTarget.value, 10) : null;

    this.setState(
      {
        search: {
          ...this.state.search,
          cfcRaceId
        }
      },
      () => this.refreshList()
    );
  };

  /**
   * Lorsqu'on saisit une valeur dans le champ de recherche, on filtre les inscriptions en fonction de cette valeur
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le champ de recherche
   */
  onChangeOnSearch = event => {
    const searchValue = event.currentTarget.value.toLowerCase();

    if (searchValue) {
      this.setState({
        search: {
          ...this.state.search,
          searchValue
        }
      });
    }

    this.refreshList();
  };

  /**
   * Permet de filtrer la liste des inscriptions en fonction de la catégorie sélectionnée et de la valeur saisie dans le champ de recherche
   */
  refreshList() {
    let filteredCfcRegistrations = this.state.cfcRegistrations;

    this.setState(
      {
        filteredCfcRegistrations: this.state.cfcRegistrations
      },
      () => {
        filteredCfcRegistrations = this.state.filteredCfcRegistrations.filter(cfcRegistration => {
          const teamName = cfcRegistration.team_name ? cfcRegistration.team_name.toLowerCase() : "";
          const teams = cfcRegistration.teams ? cfcRegistration.teams.toLowerCase() : "";
          const athleteName = `${cfcRegistration.user.firstname} ${cfcRegistration.user.lastname}`.toLowerCase();
          const categoryName = cfcRegistration.cfc_category?.name?.toLowerCase() || "";
          const raceName = cfcRegistration.cfc_race?.name?.toLowerCase() || "";
          const boxName = (cfcRegistration.user.coordinate?.name || this.props.noCoordinateLabel).toLowerCase();
          const { cfcCategoryId, cfcRaceId, searchValue } = this.state.search;
          const matchesCategory = cfcCategoryId ? cfcRegistration.cfcCategoryId === cfcCategoryId : true;
          const matchesRace = cfcRaceId ? cfcRegistration.cfcRaceId === cfcRaceId : true;
          const matchesSearch = searchValue ? teamName.includes(searchValue) || teams.includes(searchValue) || athleteName.includes(searchValue) || categoryName.includes(searchValue) || raceName.includes(searchValue) || boxName.includes(searchValue) : true;
          return matchesCategory && matchesRace && matchesSearch;
        });

        this.setState({
          filteredCfcRegistrations
        });
      }
    );
  }

  /**
   * Permet de gérer le clic sur le badge de check-in d'une inscription
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   */
  onClickOnCheckinBadge(cfcRegistration) {
    const apiUrl = `/api/_changeboolean/cfc-registration/${cfcRegistration.id}/checkin/${cfcRegistration.checkin ? 0 : 1}`;

    fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      }
    })
      .then(response => response.json())
      .then(async data => {
        const cfcRegistrations = await this.fetchCfcRegistrations();

        this.setState({ cfcRegistrations, filteredCfcRegistrations: cfcRegistrations });
      });
  }

  /**
   * Permet de gérer le clic sur le badge de validité du numéro PPS
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   */
  onClickOnLicenceNumberValidityBadge(cfcRegistration) {
    const apiUrl = `/api/_changeboolean/cfc-registration/${cfcRegistration.id}/licence_number_is_valid/${cfcRegistration.licence_number_is_valid ? 0 : 1}`;

    fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "gestiaweb"
      }
    })
      .then(response => response.json())
      .then(async data => {
        const cfcRegistrations = await this.fetchCfcRegistrations();

        this.setState({ cfcRegistrations, filteredCfcRegistrations: cfcRegistrations });
      });
  }

  /**
   * Permet de vérifier si une inscription peut être supprimée
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   * @returns {boolean} - True si l'inscription peut être supprimée, sinon false
   */
  canDeleteRegisteredRegistration(cfcRegistration) {
    const startDate = new Date(cfcRegistration.cfc_tournament.start_date);

    startDate.setDate(startDate.getDate() - 1);
    return !cfcRegistration.checkin && new Date() <= startDate;
  }

  /**
   * Permet de récupérer l'action d'édition pour une inscription
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   * @returns {React.JSX.Element} - Élément JSX représentant l'action d'édition
   */
  getEditAction(cfcRegistration) {
    return (
      <li>
        <a href={`/cfc-registration/___edit/${cfcRegistration.id}`} className="btn btn--no-label btn--ultra-small">
          <span className="material-icons">edit</span>
        </a>
      </li>
    );
  }

  /**
   * Permet de demander à un athlète trail de renseigner à nouveau son PPS
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   * @returns {?React.JSX.Element} - Action de demande de nouveau PPS
   */
  getRequestNewPpsAction(cfcRegistration) {
    if (this.state.cfcTournament.sport_type !== SPORT_TYPE.TRAIL || cfcRegistration.licence_number_is_valid) {
      return null;
    }

    return (
      <li>
        <a href={`/cfc-registration/___request-new-pps/${cfcRegistration.id}`} data-tooltip="Demander un nouveau PPS" data-dialog-action-title="Oui, demander un nouveau PPS" data-dialog="true" data-dialog-danger="true" data-dialog-message="Cette action va supprimer le justificatif actuel, invalider le PPS et demander à l’athlète de renseigner à nouveau ses informations dans son profil. Êtes-vous sûr ?" className="btn btn--no-label btn--ultra-small btn--danger">
          <span className="material-icons">forward_to_inbox</span>
        </a>
      </li>
    );
  }

  /**
   * Permet de récupérer les actions disponibles pour une inscription complétée
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   * @returns {React.JSX.Element} - Élément JSX représentant les actions disponibles pour une inscription complétée
   */
  getActionRenderForCompletedRegistration(cfcRegistration) {
    const deleteMessage = `Attention, la suppression de l'équipe va modifier votre planning ainsi que les fiches de scores. Êtes-vous sûr de vouloir supprimer ${cfcRegistration.nameStr} ?`;

    return (
      <ul className="actionlist donthideactions">
        {this.getEditAction(cfcRegistration)}
        {this.getRequestNewPpsAction(cfcRegistration)}
        {!cfcRegistration.payed && (
          <li>
            <a href={`/cfc-registration/__send-waiting-list-email/${cfcRegistration.id}`} data-tooltip="Envoyer le lien de paiement" data-dialog-action-title="Oui, envoyer" data-dialog="true" data-dialog-message="Cette action va envoyer un e-mail à l'athlète lui permettant de finaliser son inscription. Êtes-vous sûr ?" className="btn btn--no-label btn--ultra-small">
              <span className="material-icons">send</span>
            </a>
          </li>
        )}
        {this.canDeleteRegisteredRegistration(cfcRegistration) && (
          <li>
            <a href={`/__changeboolean/cfc-registration/${cfcRegistration.id}/state/2?forcereturnurl=${encodeURIComponent(window.location.pathname)}`} data-tooltip="Supprimer cette inscription, vous y aurez toujours accès" data-dialog-action-title="Oui, supprimer" data-dialog="true" data-dialog-danger="true" data-dialog-message={deleteMessage} className="btn btn--no-label btn--ultra-small">
              <span className="material-icons">delete</span>
            </a>
          </li>
        )}
      </ul>
    );
  }

  /**
   * Permet de récupérer les actions disponibles pour une inscription en attente
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement en attente
   * @returns {React.JSX.Element} - Élément JSX représentant les actions disponibles pour une inscription en attente
   */
  getActionRenderForWaitingRegistration(cfcRegistration) {
    const deleteMessage = `Attention, la suppression de l'équipe va modifier votre planning ainsi que les fiches de scores. Par ailleurs, toute suppression est à la charge de l'organisateur, nous ne prenons pas en charge les remboursements. Êtes-vous sûr de vouloir supprimer ${cfcRegistration.nameStr} ?`;

    return (
      <ul className="actionlist donthideactions">
        {this.getEditAction(cfcRegistration)}
        {this.getRequestNewPpsAction(cfcRegistration)}
        {!cfcRegistration.checkin && (
          <li>
            <a href={`/__changeboolean/cfc-registration/${cfcRegistration.id}/state/2?returnurl=${encodeURIComponent(window.location.pathname)}`} data-dialog-action-title="Oui, supprimer" data-dialog="true" data-dialog-danger="true" data-dialog-message={deleteMessage} className="btn btn--no-label btn--ultra-small btn--danger">
              <span className="material-icons">delete</span>
            </a>
          </li>
        )}
        <li>
          <a href={`/cfc-registration/__send-waiting-list-email/${cfcRegistration.id}`} data-dialog-action-title="Oui, envoyer" data-dialog="true" data-dialog-message="Cette action va envoyer un e-mail à l'athlète lui permettant de finaliser son inscription car une place est disponible. Êtes-vous sûr qu'une place est disponible pour ajouter cette inscription ?" className="btn btn--no-label btn--ultra-small btn--success">
            <span className="material-icons">how_to_reg</span>Place disponible
          </a>
        </li>
      </ul>
    );
  }

  /**
   * Permet de récupérer les actions disponibles pour une inscription supprimée
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement supprimé
   * @returns {React.JSX.Element} - Élément JSX représentant les actions disponibles pour une inscription supprimée
   */
  getActionRenderForDeletedRegistration(cfcRegistration) {
    return (
      <ul className="actionlist donthideactions">
        {this.getEditAction(cfcRegistration)}
        {this.getRequestNewPpsAction(cfcRegistration)}
        <li>
          <a href={`/cfc-registration/__unsubscribe/${cfcRegistration.id}/${this.state.id}`} data-tooltip="Supprimer l'équipe" data-dialog-action-title="Oui, supprimer" data-dialog="true" data-dialog-danger="true" className="btn btn--no-label btn--ultra-small btn--danger">
            <span className="material-icons">delete</span>
          </a>
        </li>
        {cfcRegistration.payment_intent && (
          <li>
            <a href={`/cfc-registration/__destroyandrefund/${cfcRegistration.id}/${this.state.id}`} data-tooltip="Supprimer et rembourser" data-dialog-action-title="Oui, supprimer et remboursé" data-dialog="true" data-dialog-danger="true" className="btn btn--no-label btn--ultra-small btn--danger">
              <span className="material-icons">credit_card_off</span>
            </a>
          </li>
        )}
        {cfcRegistration.payed === 1 && (
          <li>
            <a href={`/cfc-registration/___destroyAndReplace/${cfcRegistration.id}/${this.state.id}`} data-tooltip="Supprimer et échanger avec une équipe en attente" data-dialog-action-title="Oui, supprimer et échangé avec une équipe en attente" data-dialog="true" data-dialog-danger="true" className="btn btn--no-label btn--ultra-small btn--danger">
              <span className="material-icons">swap_horiz</span>
            </a>
          </li>
        )}
      </ul>
    );
  }

  /**
   * Permet de récupérer les actions disponibles pour une inscription en fonction de son statut
   * @param {CfcRegistrationObj} cfcRegistration - Item de l'enregistrement
   * @returns {?React.JSX.Element} - Élément JSX représentant les actions disponibles pour l'inscription en fonction de son statut
   */
  getActionsRender(cfcRegistration) {
    switch (cfcRegistration.status) {
      case "COMPLETED":
      case "NONE_COMPLETED":
        return this.getActionRenderForCompletedRegistration(cfcRegistration);
      case "WAITING_LIST":
        return this.getActionRenderForWaitingRegistration(cfcRegistration);
      case "DELETED":
        return this.getActionRenderForDeletedRegistration(cfcRegistration);
      default:
        return null;
    }
  }

  render() {
    return (
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gridGap: "16px", height: "100%" }}>
        <div className="flexline flexline--margin flexline--no-mobile">
          {this.state.cfcTournament.sport_type === SPORT_TYPE.FUNCTIONAL_FITNESS ||
            (this.state.cfcTournament.sport_type === SPORT_TYPE.HYBRID_RACE && (
              <select className="input-group__input input-group__input--with-small" onChange={this.onChangeOnSelectCategory.bind(this)}>
                <option value="" key="all">
                  Toutes
                </option>
                {this.state.cfcCategories.map((cfcCategory, index) => (
                  <option key={index} value={cfcCategory.id}>
                    {cfcCategory.name}
                  </option>
                ))}
              </select>
            ))}
          {this.state.cfcTournament.sport_type === SPORT_TYPE.TRAIL && (
            <select className="input-group__input input-group__input--with-small" onChange={this.onChangeOnSelectRace.bind(this)}>
              <option value="" key="all">
                Toutes
              </option>
              {this.state.cfcRaces.map((cfcRace, index) => (
                <option key={index} value={cfcRace.id}>
                  {cfcRace.name}
                </option>
              ))}
            </select>
          )}
          <input type="text" className="input-group__input input-group__input--with-small" aria-label="Recherche" placeholder="Rechercher..." onChange={this.onChangeOnSearch.bind(this)} />
        </div>
        <div className="table table--responsive" style={{ height: "100%", overflowY: "auto" }}>
          <table className="table__table table__table--td-bordered">
            <thead>
              <tr className="table__tr">
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky table__th--max-width">Équipe / Athlète</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Check-in</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">{this.state.cfcTournament.sport_type === SPORT_TYPE.TRAIL ? "Distances" : "Catégories"}</th>
                {this.state.cfcTournament.sport_type === SPORT_TYPE.TRAIL && (
                  <>
                    <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">PPS valide</th>
                    <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Numéro PPS</th>
                    <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Justificatif PPS</th>
                  </>
                )}
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Box / Club</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Date d'inscription</th>
                <th className="table__th table__th table__th-sticky table__th--align-left table__th--main table__th table__th-sticky">Status</th>
                <th className="table__th table__th table__th-sticky table__th--main table__th table__th-sticky--last">Actions</th>
              </tr>
            </thead>
            <tbody>
              {this.state.filteredCfcRegistrations.map((cfcRegistration, index) => (
                <tr>
                  <td className="table__td table__td--align-left table__td--max-width">
                    <span dangerouslySetInnerHTML={{ __html: cfcRegistration.nameHtmlStr }}></span>
                    <span dangerouslySetInnerHTML={{ __html: cfcRegistration.teammatesHtmlStr }}></span>
                  </td>
                  <td className="table__td table__td--align-left">
                    <>
                      {cfcRegistration.checkin ? (
                        <div className="badge badge--success badge--clickable" onClick={this.onClickOnCheckinBadge.bind(this, cfcRegistration)}>
                          Enregistré
                        </div>
                      ) : (
                        <div className="badge badge--danger badge--clickable" onClick={this.onClickOnCheckinBadge.bind(this, cfcRegistration)}>
                          Pas enregistré
                        </div>
                      )}
                    </>
                  </td>
                  <td className="table__td table__td--align-left">{this.state.cfcTournament.sport_type === SPORT_TYPE.TRAIL ? cfcRegistration.cfc_race?.name : cfcRegistration.cfc_category?.name}</td>
                  {this.state.cfcTournament.sport_type === SPORT_TYPE.TRAIL && (
                    <>
                      <td className="table__td table__td--align-left">
                        {cfcRegistration.licence_number_is_valid ? (
                          <div className="badge badge--success badge--clickable" onClick={this.onClickOnLicenceNumberValidityBadge.bind(this, cfcRegistration)}>
                            Valide
                          </div>
                        ) : (
                          <div className="badge badge--danger badge--clickable" onClick={this.onClickOnLicenceNumberValidityBadge.bind(this, cfcRegistration)}>
                            Non valide
                          </div>
                        )}
                      </td>
                      <td className="table__td table__td--align-left">{cfcRegistration.licence_number || "-"}</td>
                      <td className="table__td table__td--align-left">
                        {cfcRegistration.licence_number_justificatif ? (
                          <a href={cfcRegistration.licence_number_justificatif} target="_blank" rel="noopener noreferrer">
                            Vérifier
                          </a>
                        ) : "-"}
                      </td>
                    </>
                  )}
                  <td className="table__td table__td--align-left">{cfcRegistration.user.coordinate?.name || this.props.noCoordinateLabel}</td>
                  <td className="table__td table__td--align-left">{formatDate(cfcRegistration.createdAt, true)}</td>
                  <td className="table__td table__td--align-left">
                    <span className={STATUS_BADGE_CLASSES[cfcRegistration.status] || "badge"}>{cfcRegistration.statusStr}</span>
                  </td>
                  <td className="table__td table__td-sticky--last">{this.getActionsRender(cfcRegistration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

var cfcRegistrationsBackNode = document.getElementById("cfc-registrations-back");
if (cfcRegistrationsBackNode) {
  var root = ReactDOM.createRoot(cfcRegistrationsBackNode);
  root.render(<CfcRegistrationsBack id={cfcRegistrationsBackNode.getAttribute("data-id")} noCoordinateLabel={cfcRegistrationsBackNode.getAttribute("data-nocoordinatelabel")} />);
}
